/**
 * Gli ordini "da sentire" della scheda Biglietti: chi ha pagato ma non ha
 * ricevuto l'email per registrare i biglietti, o l'ha ricevuta e dopo giorni
 * non ha ancora registrato. "Consegnata" vuol dire che il server di posta
 * l'ha presa, non che la persona l'ha vista (puo' essere nello spam): per
 * questo conta anche la registrazione che non arriva.
 */

import type { OrdineConBiglietti } from './biglietti';

/** Un'email della sequenza di registrazione a chi ha pagato (email_messages). */
export interface EmailAcquirente {
  contact_id: string;
  status: string;
  scheduled_at: string;
  sent_at: string | null;
  delivered_at: string | null;
  first_clicked_at: string | null;
  created_at: string;
  error: string | null;
}

/** Di ogni acquirente: il codice della sua pagina dei biglietti e le email che gli abbiamo mandato. */
export interface PostaAcquirente {
  token: string | null;
  email: EmailAcquirente[];
}

export type StatoEmailAcquirente =
  | { stato: 'nessuna' }
  | { stato: 'in_coda'; quando: string }
  | { stato: 'in_viaggio'; quando: string | null }
  /** Partita (o in coda) da ore e mai consegnata: il server di posta la sta rimandando. */
  | { stato: 'ferma'; dal: string | null }
  | { stato: 'rifiutata'; errore: string | null }
  | { stato: 'consegnata'; quando: string | null; cliccata: boolean };

export type MotivoDaSentire = 'email_rifiutata' | 'email_ferma' | 'email_mancante' | 'non_registrati';

export interface OrdineDaSentire {
  ordine: OrdineConBiglietti;
  motivo: MotivoDaSentire;
  email: StatoEmailAcquirente;
  daRegistrare: number;
  /** Il codice della pagina dei biglietti di chi ha pagato: null se non e' ancora collegato. */
  token: string | null;
}

const ORA = 3600_000;
/** Gmail e gli altri consegnano in pochi secondi: dopo due ore senza conferma l'email non sta arrivando. */
const ORE_SENZA_CONSEGNA = 2;
/** Lettura degli ordini ogni 10 minuti e motore ogni 5: dopo un'ora senza email qualcosa non e' partito. */
const ORE_SENZA_EMAIL = 1;
/** Ogni tre giorni parte anche il promemoria automatico: chi non ha registrato dopo il primo si chiama. */
const GIORNI_SENZA_REGISTRAZIONE = 3;

/** Per prime le email che non arrivano, poi chi non registra. */
const GRAVITA: Record<MotivoDaSentire, number> = {
  email_rifiutata: 0,
  email_ferma: 1,
  email_mancante: 2,
  non_registrati: 3,
};

const NESSUNA_POSTA: PostaAcquirente = { token: null, email: [] };

/** Da quanti millisecondi e' passato un istante. Data mancante: zero. */
function trascorsi(iso: string | null, adesso: Date): number {
  if (!iso) return 0;
  const istante = Date.parse(iso);
  return Number.isNaN(istante) ? 0 : adesso.getTime() - istante;
}

function piuRecente(email: EmailAcquirente[], quando: (e: EmailAcquirente) => string): EmailAcquirente {
  return email.reduce((a, b) => (quando(b).localeCompare(quando(a)) > 0 ? b : a));
}

/**
 * Com'e' andata la posta a chi ha pagato. Basta un'email consegnata (la
 * prima o un promemoria) perche' il link ce l'abbia; altrimenti conta
 * l'ultima mandata.
 */
export function statoEmailAcquirente(email: EmailAcquirente[], adesso: Date): StatoEmailAcquirente {
  if (email.length === 0) return { stato: 'nessuna' };

  const arrivate = email.filter((e) => e.status === 'consegnato' || e.first_clicked_at);
  if (arrivate.length > 0) {
    const ultima = piuRecente(arrivate, (e) => e.delivered_at ?? e.sent_at ?? e.created_at);
    return {
      stato: 'consegnata',
      quando: ultima.delivered_at ?? ultima.sent_at,
      cliccata: arrivate.some((e) => e.first_clicked_at),
    };
  }

  const ultima = piuRecente(email, (e) => e.created_at);
  const ferma = (dal: string | null) => trascorsi(dal, adesso) > ORE_SENZA_CONSEGNA * ORA;
  switch (ultima.status) {
    case 'in_coda':
    case 'in_invio':
      return ferma(ultima.scheduled_at)
        ? { stato: 'ferma', dal: ultima.scheduled_at }
        : { stato: 'in_coda', quando: ultima.scheduled_at };
    case 'inviato':
      return ferma(ultima.sent_at) ? { stato: 'ferma', dal: ultima.sent_at } : { stato: 'in_viaggio', quando: ultima.sent_at };
    case 'rimbalzato':
    case 'fallito':
      return { stato: 'rifiutata', errore: ultima.error };
    default:
      // Annullata o simulata (modalita' di prova): all'acquirente non e' arrivato niente.
      return { stato: 'nessuna' };
  }
}

/** Perche' un ordine e' da sentire, o null se non c'e' niente da fare. */
export function motivoDaSentire(o: OrdineConBiglietti, email: StatoEmailAcquirente, adesso: Date): MotivoDaSentire | null {
  if (!o.biglietti.some((b) => b.stato === 'da_registrare')) return null;
  if (email.stato === 'rifiutata') return 'email_rifiutata';
  if (email.stato === 'ferma') return 'email_ferma';

  const dalPagamento = trascorsi(o.ordine.pagato_il ?? o.ordine.creato_il, adesso);
  if (email.stato === 'nessuna' && dalPagamento > ORE_SENZA_EMAIL * ORA) return 'email_mancante';
  if (dalPagamento > GIORNI_SENZA_REGISTRAZIONE * 24 * ORA) return 'non_registrati';
  return null;
}

/** La posta di chi ha pagato un ordine (vuota se l'acquirente non e' ancora collegato). */
export function postaDellOrdine(o: OrdineConBiglietti, posta: Map<string, PostaAcquirente>): PostaAcquirente {
  return (o.ordine.contact_id && posta.get(o.ordine.contact_id)) || NESSUNA_POSTA;
}

/** Gli ordini da sentire, dal piu' urgente; a parita', nell'ordine della scheda. */
export function ordiniDaSentire(
  ordini: OrdineConBiglietti[],
  posta: Map<string, PostaAcquirente>,
  adesso: Date,
): OrdineDaSentire[] {
  const elenco: OrdineDaSentire[] = [];
  for (const o of ordini) {
    const suaPosta = postaDellOrdine(o, posta);
    const email = statoEmailAcquirente(suaPosta.email, adesso);
    const motivo = motivoDaSentire(o, email, adesso);
    if (!motivo) continue;
    elenco.push({
      ordine: o,
      motivo,
      email,
      daRegistrare: o.biglietti.filter((b) => b.stato === 'da_registrare').length,
      token: suaPosta.token,
    });
  }
  return elenco.sort((a, b) => GRAVITA[a.motivo] - GRAVITA[b.motivo]);
}
